import type IntlService from 'ember-intl/services/intl';
import { warnIfNotDateOnly } from './warn-if-not-date-only';

const FRENCH_LOCALE = /^fr(?:-|$)/i;

/**
 * Formats a date as a numeric day, month and year. French locales read day first
 * ("15/01/2025"); fr-CA's own default would be year first. Other locales use the
 * intl service's numeric format unchanged ("01/15/2025" in en-US).
 *
 * @param options.timeZone pass `'UTC'` for calendar dates so the day never shifts
 * @param options.dayMonth `'numeric'` drops the leading zero on day and month
 */
export function formatNumericDate(
  date: Date,
  intl: IntlService,
  options: {
    year?: 'numeric' | '2-digit';
    dayMonth?: 'numeric' | '2-digit';
    timeZone?: string;
  } = {}
): string {
  const { year = 'numeric', dayMonth = '2-digit', timeZone } = options;
  if (FRENCH_LOCALE.test(intl.primaryLocale ?? '')) {
    const parts = new Intl.DateTimeFormat(intl.primaryLocale, {
      day: dayMonth,
      month: dayMonth,
      year,
      timeZone,
    }).formatToParts(date);
    const part = (type: Intl.DateTimeFormatPartTypes) => {
      const value = parts.find((p) => p.type === type)?.value ?? '';
      // fr-CA pads day and month even when asked for 'numeric'.
      return dayMonth === 'numeric' && type !== 'year' ? String(Number(value)) : value;
    };
    return `${part('day')}/${part('month')}/${part('year')}`;
  }
  return intl.formatDate(date, { month: dayMonth, day: dayMonth, year, timeZone });
}

/**
 * Format ISO date string (YYYY-MM-DD) to localized date using UTC timezone
 * Prevents timezone shifting issues where dates display as the previous day
 *
 * Date-only input ONLY. A full ISO instant has its time and offset discarded,
 * which can render the wrong day; such input is reported via `warnIfNotDateOnly`
 * rather than repaired, because the correct rendering depends on whether the
 * value is a calendar date or a real event. Format instants at the call site.
 *
 * @param dateStr - ISO date string or 'N/A'
 * @param intl - Ember Intl service for localization
 * @param yearFormat - Format for year display ('numeric' for 4-digit, '2-digit' for 2-digit)
 * @returns Formatted date string (e.g., "12/25/2025" or "12/25/25"; day first in French) or 'N/A'
 *
 * @example
 * formatISODateToDisplay("2025-01-15", intl, "numeric")  // Returns "01/15/2025"
 * formatISODateToDisplay("2025-01-15", intl, "2-digit")  // Returns "01/15/25"
 */
export function formatISODateToDisplay(
  dateStr: string,
  intl: IntlService,
  yearFormat: 'numeric' | '2-digit' = 'numeric'
): string {
  if (!dateStr || dateStr === 'N/A') return 'N/A';

  warnIfNotDateOnly(dateStr, 'formatISODateToDisplay');

  try {
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
      return formatNumericDate(date, intl, { year: yearFormat, timeZone: 'UTC' });
    }

    // Fallback for non-ISO dates
    const date = new Date(dateStr);
    return formatNumericDate(date, intl, { year: yearFormat, timeZone: 'UTC' });
  } catch {
    return dateStr;
  }
}
